import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { PublicLayout } from './layouts/PublicLayout';
import { LabConsoleShell } from './layouts/LabConsoleShell';
import { ClinicianShell } from './layouts/ClinicianShell';
import { AuthProvider } from './contexts/AuthContext';
import { DashboardPage } from './pages/DashboardPage';
import { LotsPage } from './pages/LotsPage';
import { LotDetailPage } from './pages/LotDetailPage';
import { QCNewPage } from './pages/QCNewPage';
import { QCDetailPage } from './pages/QCDetailPage';
import { PatientsPage } from './pages/PatientsPage';
import { PatientDetailPage } from './pages/PatientDetailPage';
import { ComparePage } from './pages/ComparePage';
import { ReportPage } from './pages/ReportPage';
import { InventoryPage } from './pages/InventoryPage';
import { ReferencesPage } from './pages/ReferencesPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { AdverseEventsPage } from './pages/AdverseEventsPage';
import { SettingsPage } from './pages/SettingsPage';
import { ImportPage } from './pages/ImportPage';
import { LoginPage } from './pages/LoginPage';
import { AuthVerifyPage } from './pages/AuthVerifyPage';
import { TrialsPage } from './pages/TrialsPage';
import { LandingPage } from './pages/LandingPage';
import { ConsolePlaceholderPage } from './pages/ConsolePlaceholderPage';
import { CopilotPage } from './features/copilot/CopilotPage';
import {
  ClinicianLogCoursePage,
  ClinicianLogPage,
  ClinicianMePage,
  ClinicianOutcomePreviewPage,
  ClinicianPatientDetailPage,
  ClinicianPatientsPage,
  ClinicianTodayPage,
} from './features/clinician/ClinicianPages';

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route element={<PublicLayout />}>
            <Route index element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/verify" element={<AuthVerifyPage />} />
          </Route>

          {/* Clinician/mobile routes — intentionally separate from the desktop lab console shell. */}
          <Route path="clinician" element={<ClinicianShell />}>
            <Route index element={<ClinicianTodayPage />} />
            <Route path="today" element={<ClinicianTodayPage />} />
            <Route path="patients" element={<ClinicianPatientsPage />} />
            <Route path="patients/:id" element={<ClinicianPatientDetailPage />} />
            <Route path="patients/:id/log-course" element={<ClinicianLogCoursePage />} />
            <Route path="patients/:id/outcome-preview" element={<ClinicianOutcomePreviewPage />} />
            <Route path="patients/:id/record-outcome" element={<ClinicianOutcomePreviewPage />} />
            <Route path="log" element={<ClinicianLogPage />} />
            <Route path="me" element={<ClinicianMePage />} />
          </Route>

          {/* Protected lab console routes */}
          <Route element={<LabConsoleShell />}>
            <Route path="console" element={<DashboardPage />} />
            <Route path="console/outcomes-registry" element={<PatientsPage />} />
            <Route path="console/outcomes-registry/:id" element={<PatientDetailPage />} />
            <Route path="console/lots" element={<LotsPage />} />
            <Route path="console/lots/:id" element={<LotDetailPage />} />
            <Route path="console/studies" element={<TrialsPage />} />
            <Route
              path="console/studies/:code"
              element={
                <ConsolePlaceholderPage
                  title="Study Detail"
                  description="Study workspace for protocol overview, cohorts, product lots, enrolled patients, and review comments."
                  bullets={['Protocol summary', 'Enrollment cohorts', 'Linked lots and outcomes']}
                />
              }
            />
            <Route
              path="console/providers"
              element={
                <ConsolePlaceholderPage
                  title="Providers"
                  description="Credentialed provider workspace for care-team rosters, outcome attribution, and clinic-level operating views."
                  bullets={['Provider directory', 'Clinic affiliations', 'Outcome cohort handoffs']}
                />
              }
            />
            <Route
              path="console/providers/:id"
              element={
                <ConsolePlaceholderPage
                  title="Provider Detail"
                  description="Provider-level workspace for site overview, administered lots, patient cohorts, and outcome pattern review."
                  bullets={['Site overview', 'Lots administered', 'Provider-scoped patients']}
                />
              }
            />
            <Route
              path="console/investigators"
              element={
                <ConsolePlaceholderPage
                  title="Investigators"
                  description="Research investigator console for study oversight, protocol participation, and evidence review ownership."
                  bullets={['Investigator roster', 'Protocol assignments', 'Evidence review queue']}
                />
              }
            />
            <Route
              path="console/investigators/:id"
              element={
                <ConsolePlaceholderPage
                  title="Investigator Detail"
                  description="Investigator profile for onboarding status, credentialed access, study participation, and review ownership."
                  bullets={['Credential checklist', 'Study participation', 'Review assignments']}
                />
              }
            />
            <Route path="console/intelligence" element={<ReportPage />} />
            <Route
              path="console/intelligence/signals/:id"
              element={
                <ConsolePlaceholderPage
                  title="Signal Detail"
                  description="Candidate signal review shell for confidence, attributes, provenance, and qualified review controls. Not an efficacy claim."
                  bullets={[
                    'Candidate signal context',
                    'Evidence provenance',
                    'Qualified review state',
                  ]}
                />
              }
            />
            <Route
              path="console/consent-access"
              element={
                <ConsolePlaceholderPage
                  title="Consent & Access"
                  description="Access-control and consent workflow hub for patient permissions, data-sharing scopes, and audit-ready review."
                  bullets={['Consent status', 'Access policies', 'Data-sharing scopes']}
                />
              }
            />
            <Route path="console/audit" element={<AuditLogPage />} />
            <Route path="console/adverse-events" element={<AdverseEventsPage />} />
            <Route path="console/adverse-events/new" element={<QCNewPage />} />
            <Route path="console/adverse-events/:id" element={<QCDetailPage />} />
            <Route path="console/compare" element={<ComparePage />} />
            <Route path="console/ai-copilot" element={<CopilotPage />} />
            <Route path="console/settings" element={<SettingsPage />} />
            <Route
              path="console/settings/team-access"
              element={
                <ConsolePlaceholderPage
                  title="Team Access"
                  description="Team and access workspace for invites, roles, permissions, and audit-ready membership review."
                  bullets={['Team members', 'Role permissions', 'Access review']}
                />
              }
            />

            {/* Compatibility redirects/pages for prior /console URLs. */}
            <Route
              path="console/qc/new"
              element={<Navigate to="/console/adverse-events/new" replace />}
            />
            <Route path="console/qc/:id" element={<QCDetailPage />} />
            <Route
              path="console/patients"
              element={<Navigate to="/console/outcomes-registry" replace />}
            />
            <Route path="console/patients/:id" element={<PatientDetailPage />} />
            <Route
              path="console/reports"
              element={<Navigate to="/console/intelligence" replace />}
            />
            <Route path="console/inventory" element={<InventoryPage />} />
            <Route path="console/references" element={<ReferencesPage />} />
            <Route path="console/audit-log" element={<Navigate to="/console/audit" replace />} />
            <Route path="console/import" element={<ImportPage />} />
            <Route path="console/trials" element={<Navigate to="/console/studies" replace />} />

            {/* Compatibility routes for prior root-level console URLs. */}
            <Route path="lots" element={<Navigate to="/console/lots" replace />} />
            <Route path="lots/:id" element={<LotDetailPage />} />
            <Route path="qc/new" element={<Navigate to="/console/adverse-events/new" replace />} />
            <Route path="qc/:id" element={<QCDetailPage />} />
            <Route path="patients" element={<Navigate to="/console/outcomes-registry" replace />} />
            <Route path="patients/:id" element={<PatientDetailPage />} />
            <Route path="compare" element={<Navigate to="/console/compare" replace />} />
            <Route path="reports" element={<Navigate to="/console/intelligence" replace />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="references" element={<ReferencesPage />} />
            <Route path="audit-log" element={<Navigate to="/console/audit" replace />} />
            <Route path="settings" element={<Navigate to="/console/settings" replace />} />
            <Route path="import" element={<ImportPage />} />
            <Route path="trials" element={<Navigate to="/console/studies" replace />} />

            {/* Legacy redirects */}
            <Route path="studies/:studyId" element={<Navigate to="/console/studies" replace />} />
            <Route path="lines" element={<Navigate to="/console/lots" replace />} />
            <Route path="lines/:lineId" element={<Navigate to="/console/lots" replace />} />
            <Route path="admin" element={<Navigate to="/console/settings" replace />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
